import { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import toast from 'react-hot-toast';
import { Plus, RefreshCw, Trash2, LogOut, Wifi, WifiOff, RotateCcw } from 'lucide-react';
import Tooltip from '../components/Tooltip';
import ajuda from '../utils/ajuda';
import { SkeletonTableRows } from '../components/Skeletons';

export default function WhatsApp() {
  const { isSuperadmin } = useAuth();
  const { t } = useT();
  const [instancias, setInstancias] = useState([]);
  const [novoNome, setNovoNome] = useState('');
  const [criando, setCriando] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrInstancia, setQrInstancia] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [popupQR, setPopupQR] = useState(null);
  const [popupSync, setPopupSync] = useState(null);
  const instanciasAnteriores = useRef(new Set());
  const intervalRef = useRef(null);

  async function carregar() {
    try {
      const [instRes, limRes] = await Promise.all([
        api.get('/whatsapp/instancias'),
        api.get('/whatsapp/limite-instancias')
      ]);

      const data = instRes.data;
      const lista = Array.isArray(data) ? data : [];
      setLimiteInfo(limRes.data);

      const comStatus = await Promise.all(lista.map(async (inst) => {
        const nome = inst?.instance?.instanceName || inst?.instanceName || inst?.name || null;
        if (!nome) return { ...inst, _nome: null, connectionState: 'unknown' };
        if (inst._naoEncontrada) return { ...inst, _nome: nome, connectionState: 'nao_encontrada' };
        try {
          const { data: s } = await api.get(`/whatsapp/instancias/${nome}/status`);
          const state = s?.instance?.state || s?.state || 'unknown';
          return { ...inst, _nome: nome, connectionState: state };
        } catch {
          return { ...inst, _nome: nome, connectionState: 'unknown' };
        }
      }));

      const listaFiltrada = comStatus.filter(i => i._nome);

      // Detecta instâncias que acabaram de conectar (estavam fora, agora estão open)
      const abertasAgora = new Set(
        listaFiltrada.filter(i => i.connectionState === 'open').map(i => i._nome)
      );
      const recemConectada = [...abertasAgora].find(n => !instanciasAnteriores.current.has(n));
      if (recemConectada && !primeiraCarga) {
        setPopupSync(recemConectada);
      }
      instanciasAnteriores.current = abertasAgora;

      setInstancias(listaFiltrada);
    } catch (err) {
      console.error('Erro ao carregar instâncias:', err);
      setInstancias([]);
    } finally {
      setPrimeiraCarga(false);
    }
  }

  useEffect(() => {
    carregar();
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCriar(e) {
    e.preventDefault();
    if (!novoNome) { toast.error(t('whatsapp.toast_nome_obrigatorio')); return; }
    setCriando(true);
    try {
      const { data } = await api.post('/whatsapp/instancias/criar', { instanceName: novoNome });
      // O backend pode sanitizar o nome (ex.: "Brauna Marmitaria" → "Brauna-Marmitaria")
      const nomeCriado = data?.instanceName || novoNome;
      if (data?.nomeSanitizado) {
        toast.success(`${t('whatsapp.toast_criada')} (nome ajustado para "${nomeCriado}")`);
      } else {
        toast.success(t('whatsapp.toast_criada'));
      }
      setNovoNome('');
      setTimeout(carregar, 1000);
      setPopupQR(nomeCriado);
    } catch (err) {
      // Mostra a mensagem real do backend (limite de plano, nome duplicado,
      // erro da Evolution…) em vez de um toast genérico.
      const erro = err.response?.data?.erro;
      toast.error(erro || t('whatsapp.toast_erro_criar'));
    } finally {
      setCriando(false);
    }
  }

  async function handleQRCode(nome) {
    setCarregando(true);
    setQrInstancia(nome);
    setQrData(null);
    try {
      const { data } = await api.get(`/whatsapp/instancias/${nome}/qrcode`);
      setQrData(data);

      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/whatsapp/instancias/${nome}/status`);
          const state = s?.instance?.state || s?.state;
          if (state === 'open') {
            clearInterval(intervalRef.current);
            setQrData(null);
            setQrInstancia(null);
            toast.success(t('whatsapp.toast_conectado'));
            carregar();
          } else {
            const { data: novo } = await api.get(`/whatsapp/instancias/${nome}/qrcode`);
            setQrData(novo);
          }
        } catch {
          clearInterval(intervalRef.current);
        }
      }, 20000);
    } catch {
      toast.error(t('whatsapp.toast_erro_qr'));
    } finally {
      setCarregando(false);
    }
  }

  async function handleDesconectar(nome) {
    if (!window.confirm(t('whatsapp.confirm_desconectar', { nome }))) return;
    try {
      await api.delete(`/whatsapp/instancias/${nome}/desconectar`);
      toast.success(t('whatsapp.toast_desconectada'));
      carregar();
    } catch {
      toast.error(t('whatsapp.toast_erro_desconectar'));
    }
  }

  async function handleHardReset(nome) {
    if (!window.confirm(`Redefinir sessão de "${nome}"?\n\nIsso apaga e recria a instância na Evolution para limpar sessões corrompidas. O histórico de conversas é preservado. Você precisará escanear o QR Code novamente.`)) return;
    setCarregando(true);
    setQrInstancia(nome);
    setQrData(null);
    try {
      const { data } = await api.post(`/whatsapp/instancias/${nome}/hard-reset`);
      if (data.qr) setQrData(data.qr);
      toast.success('Sessão redefinida — escaneie o QR Code para reconectar.');
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/whatsapp/instancias/${nome}/status`);
          const state = s?.instance?.state || s?.state;
          if (state === 'open') {
            clearInterval(intervalRef.current);
            setQrData(null);
            setQrInstancia(null);
            toast.success(t('whatsapp.toast_conectado'));
            carregar();
          } else {
            const { data: novo } = await api.get(`/whatsapp/instancias/${nome}/qrcode`);
            setQrData(novo);
          }
        } catch { clearInterval(intervalRef.current); }
      }, 20000);
    } catch {
      toast.error('Erro ao redefinir sessão. Tente deletar e recriar a instância.');
    } finally {
      setCarregando(false);
    }
  }

  async function handleDeletar(nome) {
    if (!window.confirm(t('whatsapp.confirm_deletar', { nome }))) return;
    try {
      await api.delete(`/whatsapp/instancias/${nome}`);
      toast.success(t('whatsapp.toast_deletada'));
      if (qrInstancia === nome) { setQrData(null); setQrInstancia(null); clearInterval(intervalRef.current); }
      carregar();
    } catch {
      toast.error(t('whatsapp.toast_erro_deletar'));
    }
  }

  async function handleSincronizar(nome) {
    toast.loading(t('whatsapp.toast_sincronizando'), { id: 'sync' });
    try {
      const { data } = await api.post(`/whatsapp/sincronizar-contatos/${nome}`);
      toast.success(t('whatsapp.toast_sincronizados', { n: data.importados }), { id: 'sync' });
    } catch (err) {
      const msg = err.response?.data?.erro || t('whatsapp.toast_erro_sincronizar');
      toast.error(msg, { id: 'sync' });
    }
  }

  function statusInfo(state) {
    const map = {
      open:           { label: t('whatsapp.status_conectado'),      cls: 'badge badge-success', icon: <Wifi size={13} /> },
      connecting:     { label: t('whatsapp.status_conectando'),     cls: 'badge badge-warning', icon: <RefreshCw size={13} /> },
      close:          { label: t('whatsapp.status_desconectado'),   cls: 'badge badge-danger',  icon: <WifiOff size={13} /> },
      nao_encontrada: { label: t('whatsapp.status_nao_encontrada'), cls: 'badge badge-danger',  icon: <WifiOff size={13} /> },
      unknown:        { label: t('whatsapp.status_desconhecido'),   cls: 'badge badge-neutral', icon: <WifiOff size={13} /> },
    };
    return map[state] || map.unknown;
  }

  function extrairBase64(data) {
    return data?.base64 || data?.qrcode?.base64 || data?.qr?.base64 || data?.code || null;
  }

  const base64QR = extrairBase64(qrData);
  const atingiuLimite = limiteInfo && limiteInfo.disponiveis <= 0 && !isSuperadmin;

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('whatsapp.titulo')}</h1>
          <p className="page-h-sub">{t('whatsapp.subtitulo')}</p>
        </div>
        <div className="page-h-actions">
          <button onClick={carregar} className="btn btn-secondary btn-sm">
            <RefreshCw size={14} /> {t('whatsapp.atualizar_status')}
          </button>
        </div>
      </div>

      {/* Info do plano */}
      {limiteInfo && !isSuperadmin && (
        <div
          className={atingiuLimite ? 'info-box info-box-warn' : 'info-box info-box-success'}
          style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}
        >
          <div style={{ fontSize: '13px' }}>
            {t('whatsapp.plano_info', { plano: limiteInfo.plano, usadas: limiteInfo.usadas, limite: limiteInfo.limite })}
            {atingiuLimite && t('whatsapp.limite_atingido_sufixo')}
          </div>
          {atingiuLimite && (
            <button onClick={() => {
              window.open('https://wa.me/5561991093750?text=' + encodeURIComponent(
                t('whatsapp.upgrade_msg')
              ), '_blank');
            }} className="btn btn-sm" style={{
              background: 'linear-gradient(135deg, #16a34a, #13305f)', color: '#fff', borderColor: 'transparent'
            }}>
              {t('whatsapp.fazer_upgrade')}
            </button>
          )}
        </div>
      )}

      {/* Guia de conexão */}
      <div className="info-box" style={{ marginBottom: '1.5rem', fontSize: '13px' }}>
        <strong>{t('whatsapp.guia_titulo')}</strong>
        <ol style={{ margin: '8px 0 0 16px', lineHeight: 2 }}>
          <li><strong>{t('whatsapp.guia_desktop')}</strong>{t('whatsapp.guia_desktop_texto')}</li>
          <li><strong>{t('whatsapp.guia_celular')}</strong>{t('whatsapp.guia_celular_texto')}</li>
        </ol>
      </div>

      {/* Criar instância */}
      <div className="card" style={{ maxWidth: '520px', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 1rem', display: 'flex', alignItems: 'center' }}>
          {t('whatsapp.nova_instancia')}
          <Tooltip texto={ajuda.instancia_nome.texto} exemplo={ajuda.instancia_nome.exemplo} />
        </p>
        <form onSubmit={handleCriar} style={{ display: 'flex', gap: '10px' }}>
          <input value={novoNome} onChange={e => setNovoNome(e.target.value)}
            placeholder={t('whatsapp.nova_instancia_placeholder')} required disabled={atingiuLimite}
            className="input"
            style={{ flex: 1, opacity: atingiuLimite ? 0.5 : 1 }}
          />
          <button type="submit" disabled={criando || atingiuLimite} className="btn btn-primary">
            <Plus size={15} /> {criando ? t('whatsapp.criando') : t('whatsapp.criar')}
          </button>
        </form>
        {atingiuLimite && (
          <p style={{ fontSize: '11px', color: 'var(--color-warning)', marginTop: '8px' }}>
            {t('whatsapp.limite_aviso')}
          </p>
        )}
      </div>

      {/* QR Code */}
      {qrData && (
        <div className="card" style={{ maxWidth: '420px', marginBottom: '1.5rem', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 0.25rem' }}>
            {t('whatsapp.qr_titulo', { nome: qrInstancia })}
          </p>
          <p className="muted" style={{ fontSize: '12px', marginBottom: '0.5rem' }}>
            {t('whatsapp.qr_passos')}
          </p>
          {base64QR ? (
            <>
              <img
                src={base64QR.startsWith('data:') ? base64QR : `data:image/png;base64,${base64QR}`}
                alt="QR Code"
                style={{ width: '260px', height: '260px', borderRadius: 'var(--radius)' }}
              />
              <div style={{ marginTop: '0.75rem', padding: '10px 14px', background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                {t('whatsapp.qr_celular')}
              </div>
              <a
                href={base64QR.startsWith('data:') ? base64QR : `data:image/png;base64,${base64QR}`}
                download={`qrcode-${qrInstancia}.png`}
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '0.5rem', display: 'inline-block' }}
              >
                {t('whatsapp.qr_baixar')}
              </a>
            </>
          ) : (
            <div className="empty">{t('whatsapp.qr_indisponivel')}</div>
          )}
          <p className="muted" style={{ fontSize: '11px', marginTop: '0.75rem' }}>{t('whatsapp.qr_auto_atualiza')}</p>
          <button onClick={() => { setQrData(null); setQrInstancia(null); clearInterval(intervalRef.current); }}
            className="btn btn-secondary btn-sm" style={{ marginTop: '0.5rem' }}>
            {t('whatsapp.fechar')}
          </button>
        </div>
      )}

      {/* Lista de instâncias */}
      <div className="card card-flush">
        <table className="table">
          <thead>
            <tr>
              {[t('whatsapp.col_instancia'), t('whatsapp.col_status'), t('whatsapp.col_acoes')].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {primeiraCarga && instancias.length === 0 && <SkeletonTableRows rows={3} cols={3} />}
            {!primeiraCarga && instancias.map(inst => {
              const nome = inst._nome;
              const status = statusInfo(inst.connectionState);
              return (
                <tr key={nome}>
                  <td style={{ fontWeight: 500 }}>📱 {nome}</td>
                  <td>
                    <span className={status.cls}>
                      {status.icon} {status.label}
                    </span>
                  </td>
                  <td>
                    <div className="row-tight" style={{ flexWrap: 'wrap', gap: '6px' }}>
                      {inst.connectionState !== 'open' && (
                        <button onClick={() => handleQRCode(nome)} disabled={carregando}
                          className="btn btn-secondary btn-sm">
                          {t('whatsapp.btn_qrcode')}
                        </button>
                      )}
                      {inst.connectionState === 'open' && (
                        <button onClick={() => handleSincronizar(nome)} className="btn btn-info btn-sm">
                          <RefreshCw size={13} /> {t('whatsapp.btn_sincronizar')}
                        </button>
                      )}
                      {inst.connectionState === 'open' && (
                        <button onClick={() => handleDesconectar(nome)} className="btn btn-warning btn-sm">
                          <LogOut size={13} /> {t('whatsapp.btn_desconectar')}
                        </button>
                      )}
                      <button onClick={() => handleHardReset(nome)} className="btn btn-warning btn-sm" disabled={carregando}
                        title="Apaga e recria a sessão Evolution para corrigir envios que aparecem como enviados mas não chegam ao contato">
                        <RotateCcw size={13} /> Redefinir sessão
                      </button>
                      <button onClick={() => handleDeletar(nome)} className="btn btn-danger btn-sm">
                        <Trash2 size={13} /> {t('whatsapp.btn_deletar')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!primeiraCarga && instancias.length === 0 && (
              <tr><td colSpan={3}>
                <div className="empty">{t('whatsapp.vazio')}</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Popup pós-criação: oferece conectar via QR */}
      {popupQR && (
        <div className="modal-backdrop" onClick={() => setPopupQR(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" onClick={e => e.stopPropagation()}
            style={{ maxWidth: '440px', width: '90%', padding: '1.5rem', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '18px' }}>{t('whatsapp.popup_qr_titulo')}</h3>
            <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: '0 0 1.25rem' }}>
              {t('whatsapp.popup_qr_texto', { nome: popupQR })}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setPopupQR(null)} className="btn btn-secondary btn-sm">
                {t('whatsapp.popup_qr_depois')}
              </button>
              <button onClick={() => { const n = popupQR; setPopupQR(null); handleQRCode(n); }}
                className="btn btn-primary btn-sm">
                {t('whatsapp.popup_qr_botao')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup pós-conexão: oferece sincronizar contatos */}
      {popupSync && (
        <div className="modal-backdrop" onClick={() => setPopupSync(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" onClick={e => e.stopPropagation()}
            style={{ maxWidth: '440px', width: '90%', padding: '1.5rem', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '18px' }}>{t('whatsapp.popup_sync_titulo')}</h3>
            <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: '0 0 1.25rem' }}>
              {t('whatsapp.popup_sync_texto')}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setPopupSync(null)} className="btn btn-secondary btn-sm">
                {t('whatsapp.popup_sync_depois')}
              </button>
              <button onClick={() => { const n = popupSync; setPopupSync(null); handleSincronizar(n); }}
                className="btn btn-primary btn-sm">
                <RefreshCw size={13} /> {t('whatsapp.popup_sync_botao')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
